import { DefaultOptionType, LabeledValue, SelectValue, RawValue } from 'antd/es/select'
import isArray from 'lodash/isArray'
import isNumber from 'lodash/isNumber'
import isObject from 'lodash/isObject'
import isString from 'lodash/isString'
import uniqueId from 'lodash/uniqueId'

import { roundFloat } from 'Shared/utils'
import { renderEntityToIdName } from 'Shared/utils/renders'

import { EMPTY_AMOUNT, GROUP_KEY } from 'Core/constants'
import {
	ConfigItem,
	FormatOptionsConfigType,
	FormatOptionsType,
	LAST_NAME_MAX_LENGTH,
	SearchConfigType,
	SearchResult
} from 'Core/interfaces'

export const entityToStringIdName = (entity: Entity) => `${entity.id} - ${entity.name}`

export const emptyCustomLabelValue = (label: string): LabelValue => ({
	label,
	value: ''
})

export const emptyDisabledLabelValue = (label: string): LabelValue & { disabled: boolean } => ({
	label,
	value: '',
	disabled: true
})

export const customLabelValue = (label: string, value: string): LabelValue => ({
	label,
	value
})

export const deleteSubstring = (string: string, substring: string) => {
	return string.replace(substring, '')
}

export const entityToLabelValueWithId = (entity: Entity): LabelValue<number> => ({
	label: entityToStringIdName(entity),
	value: entity.id
})

export const nullableEntityToLabelValue = (entity: NullableEntity): LabelValue<number> => ({
	label: renderEntityToIdName(entity),
	value: entity.id
})

export const entityToLabelId = (entity: Entity): LabelValue<number> => ({
	label: entity.id.toString(),
	value: entity.id
})

export const entityToLabelStringId = (entity: Entity): LabelValue => ({
	label: entity.id.toString(),
	value: entity.id.toString()
})

export const entityToLabelValue = (entity: Entity): LabelValue<number> => ({
	label: entity.name,
	value: entity.id
})

export const entityToLabelValueWithDisabled = (entity: Entity, disabled: boolean) => ({
	label: `${entity.id} - ${entity.name}`,
	value: entity.id,
	key: uniqueId(),
	disabled
})

export const entityToLabelValueDisabled = (entity: Entity): LabelValueDisabled => ({
	label: entity.name,
	value: entity.id.toString(),
	disabled: true
})

export const entityToLabelValueOrNull = (entity: Entity | null): LabelValue<number> | null => {
	if (entity) {
		return {
			label: entity.name,
			value: entity.id
		}
	}

	return null
}

export const entityToLabelWithDashValue = (entity: Entity): LabelValue => ({
	label: `${entity.id} - ${entity.name}`,
	value: entity.id.toString()
})

export const entityToStringLabelValue = (entity: Entity): LabelValue => ({
	label: entity.name,
	value: entity.id.toString()
})

export const entityToLabelValueWithMetadata = <T extends Entity = Entity>(
	entity: T,
	metadataKeys?: (keyof T)[]
): LabelValueWithMetadata<number> => {
	const base: LabelValueWithMetadata<number> = {
		label: entity.name,
		value: entity.id
	}

	if (metadataKeys && metadataKeys.length > 0) {
		base.metadata = {}
		metadataKeys.forEach((key) => {
			base.metadata![String(key)] = entity[key]
		})
	}

	return base
}

export const entityToNameInValueLabelValue = (entity: Entity): LabelValue => ({
	label: entity.name,
	value: entity.name
})

export const entityToString = (
	entity?: { name?: Entity['name'] | null; id?: Entity['id'] | null } | null
): string => {
	if (!entity) {
		return ''
	}
	return [entity.id, entity.name].filter((v) => v).join(' - ')
}

export const stringToEntity = (
	entityString: BasicString,
	includeAfterSecondDash: boolean = false
): Entity | null => {
	if (!entityString) {
		return null
	}

	const parts = entityString.split(' - ')

	if (parts.length < 2) {
		return null
	}

	const id = parseFloat(parts[0].trim())

	const name = includeAfterSecondDash ? parts.slice(1).join(' - ') : parts[1]

	return { id, name }
}

export const entityToRoundBracketsFormatString = (entity: Entity | null): string | null => {
	if (!entity) {
		return null
	}

	return `${entity.name} (${entity.id})`
}

export const getEntityFromRoundBracketsFormatString = (str: string | null): Entity | null => {
	if (!str) {
		return null
	}

	const startBracketIndex = str.lastIndexOf('(')

	if (!startBracketIndex || startBracketIndex === -1) {
		return null
	}

	const lastBracketIndex = str.lastIndexOf(')')

	if (lastBracketIndex === -1) {
		return null
	}

	const substringId = str.slice(startBracketIndex + 1, lastBracketIndex)
	const id = parseInt(substringId)

	if (isNaN(id)) {
		return null
	}

	const name = str.slice(0, startBracketIndex - 1).trim()

	return { id, name }
}

export const valueToStringLabelValue = (value: number): LabelValue => ({
	label: value.toString(),
	value: value.toString()
})

export const valueToLabelNumberValue = (value: number): LabelValue<number> => ({
	label: value.toString(),
	value: value
})

export const valueToLabelValue = (value: string): LabelValue => ({
	label: value,
	value: value
})

export const valueOrLabelValueToNullable = <T extends string | number>(
	value: T | LabelValue<T> | null | undefined
): T | null => {
	const parsedValue =
		isNumber(value) || isString(value)
			? value
			: value?.value

	return parsedValue === '' ? null : parsedValue ?? null
}

export const NameCodeToLabelValue = (item: NameCode): LabelValue => ({
	label: item.name,
	value: item.code
})

export const IdCodeToLabelValue = (item: IdCode): LabelValue => ({
	label: item.code.toString(),
	value: item.id.toString()
})

export const NameCodeToLabelNumberValue = (item: NameCode): LabelValue<number> => ({
	label: item.name,
	value: parseInt(item.code)
})

export const codeToLabelValue = (item: {
	name: string
	code: string
	id: number
}): LabelValue<number> => ({
	label: `${item.code} - ${item.name}`,
	value: item.id
})

export const idCodeToLabelValue = (item: IdCode): LabelValue<number> => ({
	label: item.code,
	value: item.id
})

export const requiredFieldLabel = (label: string) => `${label}*`

export const formatDataToLabelValueWithId = (data: Entity[]): LabelValue<number>[] =>
	data.map(entityToLabelValueWithId)

export const formatValueToStringLabelValue = (data: number[]): LabelValue<number>[] =>
	data.map(valueToLabelNumberValue)

export const formatDataToLabelValue = (data: Entity[]): LabelValue<number>[] =>
	data.map(entityToLabelValue)

export const formatDataToChargeTypeGLAccountLabelValue = (
	entities: ChargeTypesWithGLAccount[]
): LabelValue<number>[] =>
	entities.map((entity) => ({
		label: `${entity.name} - ${entity.glAccountNumber}`,
		value: entity.id
	}))

export const formatIdDashNameDataToLabelValue = (data: Entity[]): LabelValue<string>[] =>
	data.map(entityToLabelWithDashValue)

export const formatNameCodesToLabelValue = (data: NameCode[]): LabelValue[] =>
	data.map(NameCodeToLabelValue)

export const formatNameCodesToLabelNumberValue = (data: NameCode[]): LabelValue<number>[] =>
	data.map(NameCodeToLabelNumberValue)

export const formatIdCodeToLabelValue = (data: IdCode[]): LabelValue<number>[] =>
	data.map(idCodeToLabelValue)

export const wrapInGroup = (label: string, options: LabelValue[]): GroupedLabelValue[] => [
	{
		label,
		options
	}
]

export const getFormatFuncByName =
	(name: string) =>
	({ searchById, searchByName }: SearchResult) =>
		[
			{
				label: `${name} ID`,
				options: formatDataToLabelValueWithId(searchById)
			},
			{
				label: `${name} Name`,
				options: formatDataToLabelValue(searchByName)
			}
		]

export const getFormatFuncByNameWithIdValues =
	(name: string) =>
	({ searchById, searchByName }: SearchResult) =>
		[
			{
				label: `${name} ID`,
				options: formatDataToLabelValueWithId(searchById)
			},
			{
				label: `${name} Name`,
				options: formatDataToLabelValueWithId(searchByName)
			}
		]

export const makeHeaderTitle = <T extends { name: Key | Nullish; id: Key | Nullish }>(
	data: T | null
) => {
	if (!data?.name && !data?.id) {
		return ''
	}

	if (data?.name && !data?.id) {
		return `${data.name}`
	}

	return `${data.name} [${data.id}]`
}

export const entityToStringPreviewValue = (data: Entity | null | undefined) => {
	if (!data) {
		return ''
	}

	return `${data.name} (${data.id})`
}

export function entityToLabelValueExtra<T>(entity: Entity, callback: (name: string) => T) {
	return {
		label: entity.name,
		value: entity.id,
		extra: callback(entity.name)
	}
}

export function entityToLabelValueExtraName(entity: Entity) {
	return {
		label: entity.name,
		value: entity.id,
		extra: entity.name
	}
}

export const entityToLabelValueExtraWithId = (entity: Entity) => ({
	label: entityToStringIdName(entity),
	value: entity.id,
	extra: entity.id,
	key: `${entity.id} - ${entity.name}`
})

export const formatDataToLabelValueExtraWithId = (data: Entity[]) =>
	data.map(entityToLabelValueExtraWithId)

export const entityToLabelValueExtraId = (entity: Entity) => ({
	label: entity.name,
	value: entity.id,
	extra: entity.id,
	key: `${entity.id} - ${entity.name}`
})

export const formatDataToLabelValueExtra = (data: Entity[]) => data.map(entityToLabelValueExtraId)

export const entityToCodeDescription = (item: CodeDescription): LabelValue<number> => ({
	label: `${item.code} - ${item.description}`,
	value: item.id
})

export const entityToCodeDescriptionOrNull = (
	item: CodeDescription | null
): LabelValue<number> | null => {
	if (!item?.code || !item?.id || !item?.description) {
		return null
	}

	return {
		label: `${item.code} - ${item.description}`,
		value: item.id
	}
}

export const stringToBase64 = (data: BasicString) => btoa(unescape(encodeURIComponent(data || '')))

export const filterOptions = (input: string, option?: DefaultOptionType) =>
	option ? option.label!.toString().toLowerCase().indexOf(input.toLowerCase()) >= 0 : false

export const valueDescriptionToLabelValue = (entity: ValueDescription): LabelValue => ({
	label: entity.description,
	value: entity.value
})

export const formatValueDescriptionDataToLabelValue = (data: ValueDescription[]): LabelValue[] =>
	data.map(valueDescriptionToLabelValue)

export const formatValueDescriptionDataToLabelValueId = (data: ValueDescription[]): LabelValue[] =>
	data.map(valueDescriptionToLabelValueId)

export const valueDescriptionToLabelValueId = (entity: ValueDescription): LabelValue => ({
	label: `${entity.value} - ${entity.description}`,
	value: entity.value
})

export const withPercentage = (value: string | number | null | undefined) =>
	isNumber(value) ? `${roundFloat(value)}%` : EMPTY_AMOUNT

export const stringToLabelValue = (value: string): LabelValue => ({
	label: value,
	value: value
})

export const valueToGroupValue = (value: string, groupKey: string): string =>
	`<${GROUP_KEY}:${groupKey}>${value}`

export const parseGroupValueToValue = (groupValue: string | number): string | number => {
	if (isNumber(groupValue)) {
		return groupValue
	}

	if (!groupValue.includes(GROUP_KEY)) {
		return groupValue
	}

	const startIndex = groupValue.indexOf('>') + 1
	const endIndex = groupValue.length

	const slicedValue = groupValue.slice(startIndex, endIndex)
	const formattedValue = parseFloat(slicedValue)

	return `${formattedValue}` === slicedValue ? formattedValue : slicedValue
}

export const parseSelectValue = (value: SelectValue, valueOnly?:boolean): SelectValue => {
	if (!value || isNumber(value)) {
		return value
	}

	if (isString(value)) {
		return parseGroupValueToValue(value)
	}

	if (isArray(value)) {
		if (isObject(value[0])) {
			return value.map((item) => {
				const labelValueItem = item as LabeledValue

				return valueOnly? parseGroupValueToValue(labelValueItem.value) as RawValue : {
					...labelValueItem,
					value: parseGroupValueToValue(labelValueItem.value)
				}
			})
		}

		return value
	}

	return valueOnly? parseGroupValueToValue(value.value) : {
		...value,
		value: parseGroupValueToValue(value.value)
	}
}

export const parseDefaultOptionWithGroupValue = (option: DefaultOptionType): DefaultOptionType => ({
	...option,
	value: option.value ? parseGroupValueToValue(option.value) : option.value
})

export const parseOptionWithGroupValue = (
	option: DefaultOptionType | DefaultOptionType[] | undefined
): DefaultOptionType | DefaultOptionType[] | undefined => {
	if (!option) {
		return option
	}

	if (isArray(option)) {
		return option.map(parseDefaultOptionWithGroupValue)
	}

	return parseDefaultOptionWithGroupValue(option)
}

export const applySuffixToLabel = <Config, OptionResponse, F extends {}>(
	formattedOptions: FormattedOptions[],
	groupOptions: OptionResponse,
	config: Config
) => {
	const configType = config as FormatOptionsConfigType<F>

	const groupLabelsWithSuffix = Object.values<ConfigItem<F>>(configType).reduce(
		(acc, value) => (!!value.suffixData ? ([...acc, value.label] as string[]) : acc),
		[] as string[]
	)

	return formattedOptions.map((formattedOption) => {
		if (formattedOption.label && !groupLabelsWithSuffix.includes(formattedOption.label)) {
			return formattedOption
		}

		const optionsType = groupOptions as FormatOptionsType<F>

		return {
			...formattedOption,
			options: formattedOption.options.map((option) => {
				const [parsedGroupKeyString, id] = option.value.split('-')
				const parsedGroupKey = parsedGroupKeyString as keyof typeof configType

				const entity = optionsType[parsedGroupKey].find(
					(initialOption) => (initialOption as Entity<string>).id.toString() === id
				)

				if (!entity) {
					return option
				}

				return {
					...option,
					label: configType[parsedGroupKey].suffixData?.render(entity as Entity),
					hasSuffix: configType[parsedGroupKey].suffixData?.checkHasSuffix(
						entity as Entity
					)
				}
			})
		}
	})
}

export const entityToContactLabelValue = (
	entity: Entity & { email: BasicString; phone: BasicString }
): LabelValueExtra<number> => ({
	label: `${entity.name}, ${entity.email}`,
	value: entity.id,
	extra: entity.name
})

export const entityToLabelValueWithExtraIdName = (entity: Entity): LabelValueExtra<number> => ({
	label: entity.name,
	value: entity.id,
	extra: `${entity.id} - ${entity.name}`
})

export const formatServiceRequestSupplierAndCompany =
	(name: string) =>
	({ searchById, searchByName }: SearchResult) =>
		[
			{
				label: `${name} ID`,
				options: formatDataToLabelValueWithId(searchById)
			},
			{
				label: `${name} Name`,
				options: searchByName.map(entityToLabelValueWithExtraIdName)
			}
		]

export const personEntityToLastFirstNameString = <
	T extends { firstName: BasicString; lastName: BasicString }
>(
	personEntity: T | null,
	useComma: boolean = true
): BasicString => {
	if (!personEntity) {
		return null
	}

	const { firstName, lastName } = personEntity

	const separator = useComma ? ', ' : ' '

	return `${lastName}${separator}${firstName}`
}

export const personEntityToLabelValue = <
	T extends { id: number; firstName: BasicString; lastName: BasicString }
>(
	entity: T,
	useComma?: boolean
): LabelValue<number> => ({
	label: personEntityToLastFirstNameString(entity, useComma) || '',
	value: entity.id
})

export const generateUserNameFromContactName = (contactName: string): string => {
	const [lastName, firstName] = contactName.split(',').map((part) => part.trim())

	const firstInitial = firstName.charAt(0).toLowerCase()

	const mergedLastName = lastName.replace(/\s+/g, '').toLowerCase()

	const lastNameSnippet = mergedLastName.substring(0, LAST_NAME_MAX_LENGTH)

	const userName = (firstInitial + lastNameSnippet).replace(/[^a-z0-9]/gi, '')

	return userName
}

export const transformValueForInput = (value: Key) => (value === '' ? null : value)

export const countryEntityToLabelValue = (entity: CodeEntity): LabelValue<number> => ({
	value: entity.id,
	label: `${entity.name} (${entity.code})`
})

export const getMainSearchQueryConfig = <T extends string>(config: SearchConfigType<T>) =>
	Object.entries(config).reduce((acc, [key, value]) => {
		acc = {
			...acc,
			[key]: {
				...value,
				isMainSearch: true
			}
		}
		return acc
	}, {} as SearchConfigType<string>)

export const renameSearchInitKeys = <Field, FormatterEntity = {}>(
	obj: SearchConfigType<Field, FormatterEntity>,
	keyMap: Record<string, string>
): SearchConfigType<Field, FormatterEntity> =>
	Object.fromEntries(Object.entries(obj).map(([key, value]) => [keyMap[key] || key, value]))
